import { FC, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  FormController,
  FormControllerCheckbox,
  MuiBox,
  MuiGrid,
  MuiSelectPhoneWithTextInput,
  MuiSelectWithTextInput,
  MuiToggleSwitch,
  OptionsDataType,
} from "../../../../../components";
import { MAX_LENGTH_75 } from "../../../../../helpers/constants";
import { commonSiteCoreKeys } from "../../../../../helpers/constants/siteCoreKeys";
import { useGridContainer } from "../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { ProgramSearchProgramFormKey } from "../../../modelsAndSchemas/models";
import { programSearchSiteCoreKeys } from "../../../siteCore/SiteCoreKeys";

interface NISSearchMoreFieldsProps {
  pageId: string;
  filterDropdownValue: OptionsDataType[];
}
export const NISSearchMoreFields: FC<NISSearchMoreFieldsProps> = ({
  pageId,
  filterDropdownValue,
}) => {
  const { gridSizeLarge: gridSize, gridSizeLargeClass } = useGridContainer();
  const { control, getValues } = useFormContext();
  const { commonLabels, useFetchLabel, fetchValidationMessage, commonValidationMessages } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const getLabel = useFetchLabel(programSearchSiteCoreKeys.searchFormKey);

  const isParticipantEmailPersonalToggleEnabled = useWatch({
    name: ProgramSearchProgramFormKey.isParticipantEmailPersonal,
    control,
  });

  const participantEmailLabel = useMemo(
    () =>
      isParticipantEmailPersonalToggleEnabled
        ? programSearchSiteCoreKeys.participantEmailWork
        : programSearchSiteCoreKeys.participantEmailPersonal,
    [isParticipantEmailPersonalToggleEnabled],
  );

  return (
    <MuiGrid container className="form-container">
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <MuiBox className="toggle-parent-container">
          <FormController
            control={control}
            name={ProgramSearchProgramFormKey.participantEmailPersonal}
          >
            <MuiSelectWithTextInput
              options={filterDropdownValue}
              label={getLabel(participantEmailLabel)}
              placeholder={commonLabels.emailPlaceHolder}
              control={control}
              selectBoxName={
                ProgramSearchProgramFormKey.participantEmailPersonalFilter
              }
              id={`${pageId}-${ProgramSearchProgramFormKey.participantEmailPersonal}`}
              selectBoxId={`${pageId}-${ProgramSearchProgramFormKey.participantEmailPersonalFilter}`}
              maxLength={MAX_LENGTH_75}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
          <FormControllerCheckbox
            control={control}
            name={ProgramSearchProgramFormKey.isParticipantEmailPersonal}
          >
            <MuiToggleSwitch className="toggle-container" />
          </FormControllerCheckbox>
        </MuiBox>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.nisParticipantMobileNumber}
        >
          <MuiSelectPhoneWithTextInput
            ignoreError
            label={getLabel(programSearchSiteCoreKeys.participantMobileNumber)}
            defaultValue={getValues(
              ProgramSearchProgramFormKey.nisParticipantMobileNumber,
            )}
            defaultSelectedCountry={getValues(
              ProgramSearchProgramFormKey.nisMobileISOCode,
            )}
            placeholder={commonLabels.telephonePlaceHolder}
            phoneNumberFormId={
              ProgramSearchProgramFormKey.nisParticipantMobileNumber
            }
            countryCodeFormId={ProgramSearchProgramFormKey.nisMobileCountryCode}
            countryIsoCodeFormId={ProgramSearchProgramFormKey.nisMobileISOCode}
            withSelectOption
            searchPlaceholder={commonLabels.search}
            searchNotFound={commonLabels.noResult}
            selectLabel={commonLabels.selectPlaceHolder}
            invalidPhoneNumberMessage={fetchValidationMessage(
              commonSiteCoreKeys.invalidPhoneNumber,
            )}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.hrContact}
        >
          <MuiSelectWithTextInput
            id={`${pageId}-${ProgramSearchProgramFormKey.hrContact}`}
            options={filterDropdownValue}
            selectBoxId={`${pageId}-${ProgramSearchProgramFormKey.hrContactFilter}`}
            placeholder={commonLabels.inputPlaceHolder}
            label={getLabel(programSearchSiteCoreKeys.hrContact)}
            selectBoxName={ProgramSearchProgramFormKey.hrContactFilter}
            control={control}
            requiredARIALabel={commonARIALabel.required}
            maxLength={MAX_LENGTH_75}
            tooltipMessage={commonValidationMessages.fieldSpecialCharactersTooltipMessage}
            tooltipHeaderTitle={
              commonValidationMessages.allowedSpecialCharactersTitle
            }
          />
        </FormController>
      </MuiGrid>
    </MuiGrid>
  );
};
